import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Mic, Square, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AudioRecorderButtonProps {
    onTranscriptionComplete: (text: string) => void;
    onDiscard?: () => void;
    disabled?: boolean;
    className?: string;
}

export function AudioRecorderButton({
    onTranscriptionComplete,
    onDiscard,
    disabled = false,
    className,
}: AudioRecorderButtonProps) {
    const { t } = useTranslation();
    const [isTranscribing, setIsTranscribing] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isLongPressing, setIsLongPressing] = useState(false);

    const {
        isRecording,
        isProcessing,
        duration,
        maxDuration,
        progress,
        startRecording,
        stopRecording,
        cancelRecording,
        hasPermission,
        requestPermission,
        error,
    } = useVoiceRecorder({
        onMaxDurationReached: () => {
            toast.info(t('handoff.maxDurationReached'));
            handleStopAndTranscribe();
        },
    });

    // Only show on native mobile platforms
    if (!Capacitor.isNativePlatform()) {
        return null;
    }

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleStartRecording = async () => {
        // If permission is null (not checked yet) or false, request it
        if (hasPermission !== true) {
            const granted = await requestPermission();
            if (!granted) {
                toast.error(t('handoff.microphonePermissionDenied'));
                return;
            }
        }

        const started = await startRecording();
        if (!started && error) {
            toast.error(error);
        }
    };

    const handleStopAndTranscribe = async () => {
        const audioBase64 = await stopRecording();

        if (!audioBase64) {
            toast.error(t('handoff.recordingFailed'));
            return;
        }

        // Immediately start transcription
        setIsTranscribing(true);

        try {
            const { data, error: transcribeError } = await supabase.functions.invoke('transcribe-audio', {
                body: {
                    audioBase64: audioBase64,
                    mimeType: 'audio/aac',
                },
            });

            if (transcribeError) {
                throw transcribeError;
            }

            if (data?.error) {
                throw new Error(data.error);
            }

            if (data?.text) {
                // Replace text completely (not append)
                onTranscriptionComplete(data.text);
                toast.success(t('handoff.transcriptionComplete'));
            } else {
                toast.warning(t('handoff.noSpeechDetected'), { duration: 5000 });
            }
        } catch (err: any) {
            console.error('Transcription error:', err);
            toast.error(err.message || t('handoff.transcriptionFailed'));
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleCancel = async () => {
        await cancelRecording();
        onDiscard?.();
    };

    // Long press handlers for WhatsApp-style recording
    const handleTouchStart = () => {
        longPressTimer.current = setTimeout(() => {
            setIsLongPressing(true);
            handleStartRecording();
        }, 200);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
        if (isLongPressing && isRecording) {
            setIsLongPressing(false);
            handleStopAndTranscribe();
        }
    };

    const handleTouchCancel = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
        if (isRecording) {
            handleCancel();
        }
        setIsLongPressing(false);
    };

    const isDisabled = disabled || isProcessing || isTranscribing;

    // Transcribing state
    if (isTranscribing) {
        return (
            <div className={cn("flex items-center justify-center gap-3 p-4 bg-muted rounded-lg", className)}>
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm font-medium">{t('handoff.transcribing')}</span>
            </div>
        );
    }

    // Recording state - WhatsApp style
    if (isRecording) {
        return (
            <div className={cn("space-y-3", className)}>
                {/* Timer and waveform indicator */}
                <div className="flex items-center justify-between p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
                        <span className="font-mono font-bold text-destructive text-lg">
                            {formatTime(duration)}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        {/* Simple waveform animation */}
                        {[...Array(5)].map((_, i) => (
                            <div
                                key={i}
                                className="w-1 bg-destructive rounded-full animate-pulse"
                                style={{
                                    height: `${Math.random() * 16 + 8}px`,
                                    animationDelay: `${i * 0.1}s`
                                }}
                            />
                        ))}
                    </div>
                </div>

                {/* Progress bar */}
                <Progress
                    value={progress}
                    className="h-1"
                    indicatorClassName={cn(
                        progress >= 90 ? "bg-destructive" :
                            progress >= 70 ? "bg-warning" :
                                "bg-destructive"
                    )}
                />

                {/* Control buttons */}
                <div className="flex gap-2">
                    <Button
                        variant="default"
                        className="flex-1 bg-primary hover:bg-primary/90"
                        onClick={handleStopAndTranscribe}
                    >
                        <Square className="w-4 h-4 mr-2 fill-current" />
                        {t('handoff.stopAndTranscribe')}
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCancel}
                        className="text-destructive hover:text-destructive"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                    {t('handoff.maxDuration')}: {formatTime(maxDuration)}
                </p>
            </div>
        );
    }

    // Processing state
    if (isProcessing) {
        return (
            <Button
                variant="outline"
                className={cn("w-full", className)}
                disabled
            >
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.processing')}
            </Button>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={cn("space-y-2", className)}>
                <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                </div>
                <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleStartRecording}
                    disabled={isDisabled}
                >
                    <Mic className="w-4 h-4 mr-2" />
                    {t('handoff.tryAgain')}
                </Button>
            </div>
        );
    }

    // Default idle state - WhatsApp style microphone button
    return (
        <Button
            variant="outline"
            className={cn("w-full", className)}
            onClick={handleStartRecording}
            disabled={isDisabled}
        >
            <Mic className="w-4 h-4 mr-2" />
            {t('handoff.recordAudio')}
        </Button>
    );
}
